import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">Privacy Policy</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Learn how AllChinaBuy Spreadsheet Resource Hub collects, uses, and protects your personal information. Last updated: August 25, 2026.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Introduction</h2>
            <p>
              AllChinaBuy Spreadsheet Resource Hub (allchinabuyspread.cyou) respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and safeguard your information when you visit our website. By using this website, you agree to the practices described in this policy.
            </p>
            <p>
              This is an independent informational website and does not process orders, payments, or handle personal transaction data related to AllChinaBuy purchases. We do not have access to your AllChinaBuy account, orders, or payment information.
            </p>

            <h2>Information We Collect</h2>
            <h3>Automatically Collected Data</h3>
            <p>
              When you visit our website, we may automatically collect certain information through standard web technologies, including:
            </p>
            <p>
              - IP address and browser type
            </p>
            <p>
              - Pages visited and time spent on each page
            </p>
            <p>
              - Referring website or search terms
            </p>
            <p>
              - Device type and operating system
            </p>
            <p>
              - Language preference and geographic location (approximate)
            </p>
            <p>
              This information is used for analytics, improving website functionality, and understanding user preferences. We do not use this data to personally identify individual users unless combined with other information you voluntarily provide.
            </p>

            <h3>Voluntarily Provided Information</h3>
            <p>
              If you contact us through our contact form or email, we may collect:
            </p>
            <p>
              - Your name and email address
            </p>
            <p>
              - The content of your message
            </p>
            <p>
              - Any other information you choose to include
            </p>
            <p>
              We use this information solely to respond to your inquiry and improve our content. We do not share contact details with third parties for marketing purposes.
            </p>

            <h2>How We Use Your Information</h2>
            <p>
              We use collected information for the following purposes:
            </p>
            <p>
              - To operate and improve the website functionality
            </p>
            <p>
              - To analyze user behavior and optimize content
            </p>
            <p>
              - To respond to user inquiries and feedback
            </p>
            <p>
              - To detect and prevent technical issues or abuse
            </p>
            <p>
              - To comply with legal obligations
            </p>
            <p>
              We do not sell, rent, or trade personal information to third parties. We do not use your data for targeted advertising beyond what is necessary for basic website analytics.
            </p>

            <h2>Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your browsing experience and collect usage data. These may include:
            </p>
            <p>
              - Essential cookies: Necessary for basic website functionality
            </p>
            <p>
              - Analytics cookies: Help us understand how visitors interact with the website
            </p>
            <p>
              - Preference cookies: Remember your language selection and other preferences
            </p>
            <p>
              You can control cookie settings through your browser preferences. Disabling cookies may affect certain website features but will not prevent you from accessing our core content.
            </p>

            <h2>Data Security</h2>
            <p>
              We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no internet-based service can guarantee absolute security. We encourage users to exercise caution when sharing personal information online.
            </p>

            <h2>Third-Party Links</h2>
            <p>
              Our website contains links to third-party websites, including AllChinaBuy (kaspreadsheet.com). We are not responsible for the privacy practices or content of these external sites. We recommend reviewing the privacy policies of any third-party websites you visit. Links to external sites are clearly marked and open in a new browser tab.
            </p>

            <h2>Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <p>
              - Right to access the personal data we hold about you
            </p>
            <p>
              - Right to request correction of inaccurate data
            </p>
            <p>
              - Right to request deletion of your data
            </p>
            <p>
              - Right to object to certain types of data processing
            </p>
            <p>
              - Right to data portability
            </p>
            <p>
              To exercise any of these rights, please contact us using the information on our Contact page.
            </p>

            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The updated policy will be posted on this page with a revised date. We encourage you to review this policy periodically.
            </p>

            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us through our <a href={`/${lang}/contact/`} className="text-primary-600 hover:underline">Contact page</a>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}